/**
 * Return cycles: a buyer can return some items of an order now and others
 * later, each within its own policy window.
 *
 * One return — its items, label, tracking and outcome — is a cycle. The
 * order's return keys (protectedData) describe the CURRENT one; the step that
 * opens the next return archives it into protectedData.returnHistory
 * (server/api-util/order-steps/returns-record.js, which decides; this only
 * shows the buyer which items can still go back).
 */
import { parseCartItems } from './dataExtractors';

const DAY_MS = 24 * 60 * 60 * 1000;

/** TradProtect's days to report a defect: every item gets at least these, a final-sale item exactly. */
export const MIN_DEFECT_DAYS = 7;
const DEFAULT_WINDOW_DAYS = 14;

/**
 * The days a defect can be reported in, from delivery: the item's return
 * window, never less than TradProtect's 7 days, and 7 days for a final-sale item
 * (client decisions 2026-10-08 and 2026-10-14). The server's rule (server/api-util/order-steps/returns-record.js
 * defectWindowDays, from server/api-util/return-windows.js) is the authority;
 * returnCycles.parity.test.js keeps the two equal.
 */
export const defectWindowDays = policy =>
  policy?.acceptsReturns === false
    ? MIN_DEFECT_DAYS
    : Math.max(
        MIN_DEFECT_DAYS,
        Number.isFinite(policy?.windowDays) ? policy.windowDays : DEFAULT_WINDOW_DAYS
      );

const RECEIVED_TRANSITIONS = [
  'transition/mark-received',
  'transition/operator-mark-received',
  'transition/auto-mark-received',
  'transition/auto-complete-no-dispute',
  'transition/customer-confirm-pickup',
  'transition/customer-confirm-arrived',
  'transition/customer-close-dispute',
  'transition/mark-received-from-disputed',
];
const FULFILMENT_TRANSITIONS = [
  'transition/mark-shipped',
  'transition/operator-mark-shipped',
  'transition/mark-picked-up',
  'transition/operator-mark-picked-up',
  'transition/mark-delivered',
  'transition/operator-mark-delivered',
];

const metadataOf = tx => tx?.attributes?.metadata || {};
const recordOf = tx => tx?.attributes?.protectedData || {};

/** The order's cart items (empty for a single-item order). */
export const cartItemsOf = tx =>
  parseCartItems(tx?.attributes?.protectedData?.cartItems).filter(item => item?.listingId);

/** Earlier returns on the order, oldest first. */
export const returnHistoryOf = tx => {
  const history = recordOf(tx).returnHistory;
  return Array.isArray(history) ? history : [];
};

/**
 * Listings that have gone back for good, with when: a return refunded, or
 * rejected with the rejection upheld. The current return counts once it has
 * such an outcome; it stays on the order until the next return archives it.
 */
const SETTLED = ['refunded', 'upheld'];
export const returnedItems = tx => {
  const returned = new Map();
  returnHistoryOf(tx)
    .filter(entry => SETTLED.includes(entry.outcome))
    .forEach(entry => (entry.items || []).forEach(item => returned.set(item.listingId, entry.closedAt || null)));
  const data = recordOf(tx);
  if (SETTLED.includes(data.returnOutcome)) {
    const current = Array.isArray(data.returnItems) ? data.returnItems : cartItemsOf(tx).filter(item => !returned.has(item.listingId));
    current.forEach(item => returned.set(item.listingId, data.returnClosedAt || null));
  }
  return returned;
};

/** When the buyer got the order: delivery scan, else received, else fulfilment. */
export const deliveredAtOf = tx => {
  const fromMetadata = metadataOf(tx).deliveredAt;
  if (fromMetadata) return new Date(fromMetadata);
  const history = tx?.attributes?.transitions || [];
  const at =
    history.find(t => RECEIVED_TRANSITIONS.includes(t.transition))?.createdAt ||
    history.find(t => FULFILMENT_TRANSITIONS.includes(t.transition))?.createdAt;
  return at ? new Date(at) : null;
};

const policyOf = (tx, item) =>
  item?.returnPolicy || tx?.attributes?.protectedData?.returnPolicy || {};

/**
 * The last day an item can go back in any form: its defect window (which is
 * never shorter than its change-of-mind window), or an operator's extension.
 */
export const returnableUntil = (tx, item) => {
  const deliveredAt = deliveredAtOf(tx);
  if (!deliveredAt) return null;
  const ends = new Date(deliveredAt.getTime() + defectWindowDays(policyOf(tx, item)) * DAY_MS);
  const extended = metadataOf(tx).returnWindowExtendedUntil;
  const ext = extended ? new Date(extended) : null;
  return ext && !isNaN(ext.getTime()) && ext > ends ? ext : ends;
};

/**
 * Each cart item with where it stands for a new return:
 *   { item, status: 'returnable' | 'returned' | 'closed', until, returnedAt }
 */
export const returnStatusOfItems = (tx, now = new Date()) => {
  const returned = returnedItems(tx);
  return cartItemsOf(tx).map(item => {
    if (returned.has(item.listingId)) {
      const at = returned.get(item.listingId);
      return { item, status: 'returned', until: null, returnedAt: at ? new Date(at) : null };
    }
    const until = returnableUntil(tx, item);
    const open = !until || now <= until;
    return { item, status: open ? 'returnable' : 'closed', until, returnedAt: null };
  });
};

